import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useWebSocket } from '../../hooks/useWebSocket';
import { alertsAPI } from '../../services/api';
import { colors } from '../../theme';
import { useTranslation } from 'react-i18next';
import AlertsPageHeader from './AlertsPageHeader';
import AlertsStatsCards from './AlertsStatsCards';
import AlertsSearchFilters from './AlertsSearchFilters';
import AlertsTableView from './AlertsTableView';
import AlertsSkeleton from './AlertsSkeleton';

const AlertsPage = () => {
  const { t } = useTranslation();
  
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('ALL');
  const [selectedType, setSelectedType] = useState('ALL');
  const [days, setDays] = useState(30);
  const [groupByTicker, setGroupByTicker] = useState(false);

  const { lastMessage, readyState } = useWebSocket('/api/alerts/ws');
  
  useEffect(() => {
    if (lastMessage) {
      try {
        const data = JSON.parse(lastMessage.data);
        if (data.type === 'alerts' && data.data) {
          setAlerts(prev => {
            // Filter out alerts without valid ticker
            const validNewAlerts = data.data.filter(
              newAlert => newAlert.ticker && newAlert.ticker.trim() !== '' && !prev.some(a => a.id === newAlert.id)
            );
            return [...validNewAlerts, ...prev].slice(0, 200);
          });
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        priority: selectedPriority === 'ALL' ? 'LOW' : selectedPriority,
        days: days
      };
      if (selectedType !== 'ALL') params.alert_type = selectedType;

      const [alertsRes, statsRes] = await Promise.all([
        alertsAPI.getRecent(params),
        alertsAPI.getSummary(days)
      ]);

      // Deduplicate alerts: keep only the latest alert per ticker per alert_date
      // If multiple alerts exist for same ticker+date, keep the one with highest score
      const alertsMap = new Map();
      const rawAlerts = alertsRes.data.alerts || [];
      
      // Filter out invalid alerts (no ticker or empty ticker)
      const validAlerts = rawAlerts.filter(alert => alert.ticker && alert.ticker.trim() !== '');
      
      validAlerts.forEach(alert => {
        const key = `${alert.ticker}_${alert.alert_date}`;
        const existing = alertsMap.get(key);
        
        if (!existing) {
          alertsMap.set(key, alert);
        } else {
          // Keep alert with higher score, or if scores are equal, keep the newer one
          const existingScore = existing.score || 0;
          const newScore = alert.score || 0;
          
          if (newScore > existingScore) {
            alertsMap.set(key, alert);
          } else if (newScore === existingScore) {
            // If scores are equal, keep the newer one based on timestamp
            const existingTime = new Date(existing.timestamp || existing.created_at || 0).getTime();
            const newTime = new Date(alert.timestamp || alert.created_at || 0).getTime();
            if (newTime > existingTime) {
              alertsMap.set(key, alert);
            }
          }
        }
      });
      
      const deduplicatedAlerts = Array.from(alertsMap.values());

      setAlerts(deduplicatedAlerts);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      setAlerts([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [days, selectedPriority, selectedType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter alerts
  let filteredAlerts = alerts.filter(alert => {
    if (selectedPriority !== 'ALL' && alert.priority !== selectedPriority) return false;
    if (selectedType !== 'ALL' && alert.alert_type !== selectedType) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        alert.ticker?.toLowerCase().includes(query) ||
        alert.company_name?.toLowerCase().includes(query) ||
        alert.sector_name?.vi?.toLowerCase().includes(query) ||
        alert.sector_name?.en?.toLowerCase().includes(query) ||
        alert.message?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Group by ticker if enabled (keep only the latest alert per ticker)
  if (groupByTicker) {
    const tickerMap = new Map();
    filteredAlerts.forEach(alert => {
      const existing = tickerMap.get(alert.ticker);
      if (!existing) {
        tickerMap.set(alert.ticker, alert);
      } else {
        // Keep the alert with the latest date, or highest priority
        const existingDate = new Date(existing.alert_date || existing.timestamp || 0);
        const newDate = new Date(alert.alert_date || alert.timestamp || 0);
        
        if (newDate > existingDate) {
          tickerMap.set(alert.ticker, alert);
        } else if (newDate.getTime() === existingDate.getTime()) {
          // Same date, prefer higher priority
          const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
          const existingPriority = priorityOrder[existing.priority] || 0;
          const newPriority = priorityOrder[alert.priority] || 0;
          if (newPriority > existingPriority) {
            tickerMap.set(alert.ticker, alert);
          }
        }
      }
    });
    filteredAlerts = Array.from(tickerMap.values());
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return colors.success.main;
      case 'MEDIUM': return colors.warning.main;
      case 'LOW': return colors.info.main;
      default: return colors.gray[500];
    }
  };

  const getAlertTypeLabel = (type) => {
    if (!type) return '';
    const key = `alerts.types.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type.replace(/_/g, ' ');
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const seconds = Math.floor((now - past) / 1000);
    if (seconds < 60) return t('alerts.timeAgo.justNow');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t('alerts.timeAgo.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('alerts.timeAgo.hours', { count: hours });
    const days = Math.floor(hours / 24);
    return t('alerts.timeAgo.days', { count: days });
  };

  const wsStatus = {
    [WebSocket.CONNECTING]: { text: t('alerts.wsStatus.connecting'), color: colors.warning.main },
    [WebSocket.OPEN]: { text: t('alerts.wsStatus.live'), color: colors.success.main },
    [WebSocket.CLOSING]: { text: t('alerts.wsStatus.closing'), color: colors.warning.main },
    [WebSocket.CLOSED]: { text: t('alerts.wsStatus.offline'), color: colors.error.main }
  }[readyState] || { text: t('alerts.wsStatus.unknown'), color: colors.gray[500] };

  const alertTypes = stats?.by_type ? Object.keys(stats.by_type) : [];

  // Professional loading skeleton
  if (loading) {
    return <AlertsSkeleton />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        <AlertsPageHeader wsStatus={wsStatus} loading={loading} onRefresh={loadData} />

        <AlertsStatsCards stats={stats} days={days} alerts={alerts} formatTimeAgo={formatTimeAgo} getAlertTypeLabel={getAlertTypeLabel} />

        <AlertsSearchFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedPriority={selectedPriority}
          setSelectedPriority={setSelectedPriority}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          days={days}
          setDays={setDays}
          alertTypes={alertTypes}
          getAlertTypeLabel={getAlertTypeLabel}
          groupByTicker={groupByTicker}
          setGroupByTicker={setGroupByTicker}
        />

        {/* Alerts View */}
        {filteredAlerts.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('alerts.emptyState.noAlertsFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('alerts.emptyState.tryAdjustingFilters')}
            </Typography>
          </Paper>
        ) : (
          <AlertsTableView
            alerts={filteredAlerts}
            getPriorityColor={getPriorityColor}
            formatTimeAgo={formatTimeAgo}
          />
        )}
      </Container>
    </Box>
  );
};

export default AlertsPage;
